import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import AuthService from '../services/AuthService';
import { API_BASE_URL } from '../constants';

const useSaveManager = () => {
    const { isAuthenticated } = useAuth();
    const [saves, setSaves] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch all saves for the current user
    const fetchSaves = useCallback(async () => {
        if (!isAuthenticated) return;

        setIsLoading(true);
        setError(null);
        try {
            const token = AuthService.getToken();
            const response = await fetch(`${API_BASE_URL}/api/saves`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch saves');
            }

            const data = await response.json();
            setSaves(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated]);

    // Create a new save
    const createSave = async (name, appState) => {
        if (!isAuthenticated) return;

        setIsLoading(true);
        setError(null);
        try {
            const token = AuthService.getToken();
            const response = await fetch(`${API_BASE_URL}/api/save`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name,
                    data: appState
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create save');
            }

            // Refresh list after saving
            await fetchSaves();
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    // Load a specific save (Get details)
    const loadSave = async (saveId) => {
        if (!isAuthenticated) return null;

        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/saves/${saveId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            });

            if (!response.ok) {
                throw new Error('Failed to load save');
            }

            const data = await response.json();
            return data.data; // Return the inner 'data' JSON object
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    // Delete a save
    const deleteSave = async (saveId) => {
        if (!isAuthenticated) return;

        setIsLoading(true);
        setError(null);
        try {
            const token = AuthService.getToken();
            const response = await fetch(`${API_BASE_URL}/api/saves/${saveId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete save');
            }

            // Refresh list after deletion
            await fetchSaves();
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    // Auto-fetch on mount if authenticated
    useEffect(() => {
        if (isAuthenticated) {
            fetchSaves();
        }
    }, [isAuthenticated, fetchSaves]);

    // Update (Overwrite) an existing save
    const updateSave = async (saveId, name, appState) => {
        if (!isAuthenticated) return;

        setIsLoading(true);
        setError(null);
        try {
            const token = AuthService.getToken();
            const response = await fetch(`${API_BASE_URL}/api/save/${saveId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name,
                    data: appState
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update save');
            }

            // Refresh list after saving
            await fetchSaves();
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        saves,
        isLoading,
        error,
        fetchSaves,
        createSave,
        updateSave,
        loadSave,
        deleteSave
    };
};

export default useSaveManager;
// Revisado: 2025-12-16
